import { PlusOutlined } from '@ant-design/icons';
import { message, Modal, Upload } from 'antd';
import type { RcFile, UploadProps } from 'antd/es/upload';
import type { UploadFile } from 'antd/es/upload/interface';
import Compressor from 'compressorjs';
import React, { useEffect, useState } from 'react';

import { IMAGE_COMPRESS_THRESHOLD_SIZE } from 'src/config';
import { tusUploadFile } from 'src/services/tus-upload';

const getBase64 = (file: RcFile): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

const compressImage = (file: RcFile) => {
  return new Promise((resolve, reject) => {
    new Compressor(file, {
      quality: 0.6,
      convertSize: 5000,
      convertTypes: ['image/png'],
      success: (result) => {
        resolve(result);
      },
      error: (err) => {
        reject(err);
      },
    });
  });
};

const beforeUpload = (file: RcFile) => {
  const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
  if (!isJpgOrPng) {
    message.error('请上传JPG或者PNG格式的图片！');
  }

  const isLt20M = file.size / 1024 / 1024 < 20;
  if (!isLt20M) {
    message.error('图片大小不能超过20MB！');
  }
  return isJpgOrPng && isLt20M ? true : Upload.LIST_IGNORE; // 文件不符合要求时，不需要在文件列表中展示
};

interface UploaderProp {
  setImages: (urls: string[]) => void;
  initImages?: string[];
  maxImages?: number;
  // 压缩阈值 kb
  compressThresholdSize?: number;
  // 大小超过 compressThresholdSize 后是否压缩
  isCompress?: boolean;
}

export function Uploader({
  setImages,
  initImages,
  maxImages = 8,
  compressThresholdSize = IMAGE_COMPRESS_THRESHOLD_SIZE,
  isCompress = false,
}: UploaderProp) {
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [fileList, setFileList] = useState<UploadFile[]>(() => {
    let imageList: UploadFile[] = [];
    if (initImages && initImages.length > 0) {
      initImages.forEach((img, i) => {
        const image: UploadFile = {
          uid: i.toString(),
          name: '处置图片' + i,
          status: 'done',
          url: img,
        };
        imageList.push(image);
      });
    }
    return imageList;
  });

  useEffect(() => {
    setImages(
      fileList.map((f) => {
        if (f.url !== undefined) {
          return f.url;
        }
        return '';
      })
    );
  }, [fileList]);

  const customRequest = async (options: any) => {
    const { file, onSuccess, onProgress, onError } = options;

    let finalFile = file;

    if (isCompress && file.size / 1024 > compressThresholdSize) {
      finalFile = (await compressImage(file)) as RcFile;
    }

    try {
      const res = await tusUploadFile({
        file: finalFile,
        onProgress: (bytesUploaded, bytesTotal) => {
          onProgress?.({ percent: (bytesUploaded / bytesTotal) * 100 });
        },
      });

      file.url = res.url;
      onSuccess(res, finalFile);
    } catch (error) {
      onError(error);
    }
  };

  const handleCancel = () => setPreviewVisible(false);

  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as RcFile);
    }

    setPreviewImage(file.url || (file.preview as string));
    setPreviewVisible(true);
    setPreviewTitle(file.name || file.url!.substring(file.url!.lastIndexOf('/') + 1));
  };

  const handleChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>添加</div>
    </div>
  );
  return (
    <>
      <Upload
        listType="picture-card"
        fileList={fileList}
        onPreview={handlePreview}
        onChange={handleChange}
        beforeUpload={beforeUpload}
        customRequest={customRequest}
        accept=".png, .jpg, .jpeg"
      >
        {fileList.length >= maxImages ? null : uploadButton}
      </Upload>
      <Modal open={previewVisible} title={previewTitle} footer={null} onCancel={handleCancel}>
        <img alt="example" style={{ width: '100%' }} src={previewImage} />
      </Modal>
    </>
  );
}
